<template>
  <div
    class="group relative flex h-20 flex-col rounded-md border p-2 pt-1"
    :class="[localElement?.format === 'extended' ? 'col-span-2' : 'col-span-1']"
  >
    <LoadingComponent v-if="isSingleElementLoading" size="small" />
    <div
      class="absolute top-0 right-0 mt-0.5 mr-0.5 opacity-0 group-hover:opacity-100"
    >
      <BaseDropDownMenu
        v-if="$can('manage_dashboards')"
        :is-button="false"
        size="xs"
        :options="actionMenuOptions"
      />
    </div>
    <div
      v-if="!isSingleElementLoading"
      class="flex flex-shrink-0 justify-center"
    >
      <div>
        <h3 class="text-center leading-none font-bold">
          {{ $trans(localElement?.title) }}
          <FontAwesomeIcon
            v-if="localElement?.custom_filters"
            :icon="['fal', 'exclamation-circle']"
            class="text-gray-400"
          />
        </h3>
      </div>
    </div>
    <div class="flex flex-1 flex-row items-center justify-center gap-2">
      <span
        v-if="
          localElement?.stat_type === 'sales_turnover' &&
          !isSingleElementLoading
        "
        class="text-2xl"
        >{{ $h.formatCurrency(localElement?.data?.base, currency) }}</span
      >
      <span v-else-if="!isSingleElementLoading" class="text-2xl">{{
        localElement?.data?.base
      }}</span>
      <template v-if="localElement?.data?.target && !isSingleElementLoading">
        <span class="text-lg text-gray-700"> /</span>
        <span class="text-2xl">{{ element.data?.target }}</span>
        <span class="text-lg text-green-600"
          >({{
            Math.round(
              (localElement?.data?.base / localElement?.data?.target) * 100,
              2
            )
          }}%)</span
        >
      </template>
    </div>
  </div>
  <ElementModal
    v-model:element="localElement"
    :open="modalOpened"
    :type="'simple'"
    :filters="filters"
    @close="handleCloseModal"
    @save-dashboard="handleSaveDashboard()"
  />
</template>

<script>
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import { mapActions, mapGetters } from 'vuex'
import ElementModal from './ElementModal.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default {
  name: 'TileSingle',
  components: {
    FontAwesomeIcon,
    LoadingComponent,
    ElementModal,
    BaseDropDownMenu
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    templateDashboardId: {
      type: String,
      required: true
    },
    dashboardRowId: {
      type: String,
      required: true
    },
    filters: {
      type: Array,
      required: false,
      default: null
    }
  },
  emits: [
    'save-dashboard',
    'update:element',
    'add-before',
    'add-after',
    'delete'
  ],
  data() {
    return {
      modalOpened: false,
      actionMenuOptions: [
        [
          {
            label: this.$t('button.edit'),
            icon: 'edit',
            gate: true,
            action: this.editElement
          },
          {
            label: this.$t('button.view'),
            icon: 'table',
            gate: true,
            action: this.changeFormat
          },
          {
            label: this.$t('button.export'),
            icon: 'file-export',
            gate: true,
            action: this.excelExport
          }
        ],
        [
          {
            label: this.$t('global.button.dashboard.analytics.add_before'),
            icon: 'plus',
            gate: true,
            action: () => this.$emit('add-before')
          },
          {
            label: this.$t('global.button.dashboard.analytics.add_after'),
            icon: 'plus',
            gate: true,
            action: () => this.$emit('add-after')
          }
        ],
        [
          {
            label: this.$t('button.delete'),
            icon: 'trash',
            gate: true,
            bg_color: 'bg-red-500',
            txt_color: 'text-white',
            action: () => this.$emit('delete')
          }
        ]
      ],
      localElement: {
        title: {},
        stat_type: null,
        period: null,
        date_to: null,
        date_from: null,
        group_by: null,
        format: 'compact'
      },
      isSingleElementLoading: false
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth',
      metadata: 'metadata/getInitialMetadata',
      currency: 'metadata/getDefaultCurrencyMetadata'
    })
  },
  watch: {
    element: {
      handler(newValue, _oldValue) {
        this.$emit('update:element', newValue)
        // this.fetchTemplateElement()
        this.isSingleElementLoading = false
      },
      deep: true
    },
    filters: {
      handler(newValue, oldValue) {
        if (!this.localElement.custom_filters) {
          this.localElement.filters = []
          for (const newValueKey in newValue) {
            if (
              newValue[newValueKey]['column'] !== null &&
              newValue[newValueKey]['condition'] !== null &&
              newValue[newValueKey]['values'] !== null &&
              newValue[newValueKey]['whereType'] !== null
            ) {
              this.localElement.filters.push(newValue[newValueKey])
            } else {
              break
            }
          }
          this.handleSaveDashboard()
        }
      },
      deep: true
    }
  },
  mounted() {
    if (!this.element.stat_type) {
      this.editElement()
    }
    this.fetchTemplateElement()
  },
  methods: {
    ...mapActions({
      processFetchStatisticsForExportAction:
        'statistics/fetchStatisticsForExport',
      processFetchTemplateDashboardElementByIdAction:
        'dashboard/fetchTemplateDashboardElementById',

      processUpdateTemplateDashboardElementByIdAction:
        'dashboard/updateTemplateDashboardElementById',

      processAddTemplateDashboardElementByIdAction:
        'dashboard/addTemplateDashboardElementById'
    }),
    editElement() {
      this.modalOpened = true
    },
    handleCloseModal() {
      this.modalOpened = false
    },
    handleSaveDashboard() {
      if (this?.localElement?.id) {
        if (!this?.localElement?.custom_filters) {
          if (this?.filters[(this?.filters).length - 1].values !== null) {
            this.localElement.filters = this?.filters
          }
        }
        this.processUpdateTemplateDashboardElementByIdAction({
          id: this?.templateDashboardId,
          element: this?.localElement
        }).then((response) => {
          this.orderedElement(response?.data?.element)
        })
      } else if (!this?.localElement?.id) {
        this.processAddTemplateDashboardElementByIdAction({
          id: this?.templateDashboardId,
          rowId: this?.dashboardRowId,
          element: this?.localElement
        }).then((response) => {
          this.orderedElement(response?.data?.element)
          this.isSingleElementLoading = false
        })
      }
    },
    excelExport() {
      this.isLoading = true
      this.processFetchStatisticsForExportAction({
        id: this.element.id,
        element: this?.localElement,
        successCallback: () => {
          this.isLoading = false
          // this.$h.toastify(this.$t('toasts.import_list_with_success'), {
          //   className: 'toastify-content success'
          // })
        },
        errorCallback: () => {
          this.$h.toastify(
            this.$t('global.error_when_import_file'),
            { className: 'toastify-content warning' },
            (this.isErrorModalOpen = true)
          )
        }
      })
    },
    fetchTemplateElement() {
      this.isSingleElementLoading = true
      if (this?.element?.row?.template_dashboard_id) {
        this.processFetchTemplateDashboardElementByIdAction({
          id: this?.element?.row?.template_dashboard_id,
          element_id: this?.element?.id
        }).then((response) => {
          this.orderedElement(response?.data?.element)
          this.isSingleElementLoading = false
        })
      }
    },
    orderedElement(element) {
      if (element.data.datasets) {
        const data = element.data.datasets[0]?.data
        const combinedArray = element.data.labels
          ?.map((label, index) => ({
            label,
            value: data[0][Object.keys(data[0])[index]]
          }))
          .filter((item) => item.value)
          .sort((a, b) => b.value - a.value)

        element.data.datasets[0].data = combinedArray.map((item) => item.value)
        element.data.labels = combinedArray.map((item) => item.label)
      }
      this.localElement = element
    }
  }
}
</script>

<style scoped></style>
